import { getRoot, resolveIdentifier, types } from 'mobx-state-tree';
import LinkType from '../types/LinkType';
import Paths from '../types/Paths';
import SectionStatusType, {
  SectionStatusTypeType,
} from '../types/SectionStatusType';
import generatePath from '../util/generatePath';

const Category = types
  .model('Category', {
    children: types.optional(types.array(types.late(() => Category)), []),
    description_html: types.maybeNull(types.string),
    discount: types.maybeNull(types.number),
    footer_description_html: types.maybeNull(types.string),
    icon: types.maybeNull(types.string),
    id: types.identifierNumber,
    image: types.maybeNull(types.string),
    mobile_image: types.maybeNull(types.string),
    is_new: types.maybeNull(types.boolean),
    link_type: types.maybeNull(types.string),
    alternate_links: types.maybeNull(types.frozen()),
    name: types.string,
    name_in_navigation: types.maybeNull(types.string),
    parent_id: types.maybeNull(types.number),
    section: types.maybeNull(
      types.model({
        status: SectionStatusTypeType,
        visible_to_crawlers: types.maybeNull(types.boolean),
      })
    ),
    SEO_description: types.maybeNull(types.string),
    SEO_title: types.maybeNull(types.string),
    show_category_filter: types.maybeNull(types.boolean),
    slug: types.string,
  })
  .views((self) => {
    return {
      /**
       * Get localized path for this category using alternate_links
       * @param {string} locale - The language code (e.g., 'fi', 'en')
       * @param {string} domain - Optional domain for multi-domain support
       * @returns {string} - Localized category path (e.g., '/urheilu/jalkapallo/c/123/')
       *
       * Note: If alternate_links contains parent_slugs array, those will be used
       * for localizing parent category slugs in the hierarchical URL structure.
       * parent_slugs[0] maps to rootProductGroup, parent_slugs[1] to productGroup, etc.
       */
      getLocalizedPath(locale, domain = null) {
        let slug = self.slug;

        // Prefer alternate_links over localized_slugs
        if (self.alternate_links) {
          const domainsToCheck = domain
            ? [domain]
            : Object.keys(self.alternate_links);

          for (const domainKey of domainsToCheck) {
            if (self.alternate_links[domainKey]) {
              const alternateLink = self.alternate_links[domainKey].find(
                (link) => link.hreflang === locale
              );
              if (alternateLink) {
                slug = alternateLink.slug;
                break;
              }
            }
          }
        }

        const categories = self.hierarchy;

        if (
          self.link_type === LinkType.EXTERNAL_LINK ||
          self.link_type === LinkType.INTERNAL_LINK
        ) {
          // For external/internal links, preserve the slug as-is
          return slug.startsWith('/') ? slug : `/${slug}`;
        }

        const getSlugForCategory = (
          category,
          locale,
          domain,
          currentCategoryIndex,
          isCurrentCategory = false
        ) => {
          // For the current category (self), check if it has parent_slugs to use for parents
          if (!isCurrentCategory && self.alternate_links) {
            const domainsToCheck = domain
              ? [domain]
              : Object.keys(self.alternate_links);

            for (const domainKey of domainsToCheck) {
              if (self.alternate_links[domainKey]) {
                const currentAlternateLink = self.alternate_links[
                  domainKey
                ].find(
                  (link) =>
                    link.hreflang === locale ||
                    link.hreflang.startsWith(locale + '-')
                );
                if (
                  currentAlternateLink &&
                  currentAlternateLink.parent_slugs &&
                  currentCategoryIndex <
                    currentAlternateLink.parent_slugs.length
                ) {
                  const parentSlug =
                    currentAlternateLink.parent_slugs[currentCategoryIndex];
                  return parentSlug.startsWith('/')
                    ? parentSlug.substring(1)
                    : parentSlug;
                }
              }
            }
          }

          // For current category or if no parent_slugs found, check category's own alternate_links
          if (category.alternate_links) {
            const domainsToCheck = domain
              ? [domain]
              : Object.keys(category.alternate_links);

            for (const domainKey of domainsToCheck) {
              if (category.alternate_links[domainKey]) {
                const alternateLink = category.alternate_links[domainKey].find(
                  (link) =>
                    link.hreflang === locale ||
                    link.hreflang.startsWith(locale + '-')
                );
                if (alternateLink && alternateLink.slug) {
                  return alternateLink.slug.startsWith('/')
                    ? alternateLink.slug.substring(1)
                    : alternateLink.slug;
                }
              }
            }
          }

          // Fallback to default slug if no alternate_links found
          return category.slug.startsWith('/')
            ? category.slug.substring(1)
            : category.slug;
        };

        return generatePath(Paths.Category, {
          id: self.id,
          rootProductGroup: getSlugForCategory(
            categories[0],
            locale,
            domain,
            0,
            categories[0] === self
          ),
          productGroup:
            categories[1] &&
            getSlugForCategory(
              categories[1],
              locale,
              domain,
              1,
              categories[1] === self
            ),
          subProductGroup:
            categories[2] &&
            getSlugForCategory(
              categories[2],
              locale,
              domain,
              2,
              categories[2] === self
            ),
        });
      },
      getNonBlockedChildren(blockedCategoriesDict) {
        return (
          self.children &&
          self.children.filter(
            (category) => !blockedCategoriesDict[category.id]
          )
        );
      },
      get ancestors() {
        const ancestors = [];
        let currentCategory = self;
        while (currentCategory.parentCategory) {
          const parent = currentCategory.parentCategory;
          ancestors.push(parent);
          currentCategory = parent;
        }
        ancestors.reverse();
        return ancestors;
      },
      get breadcrumbs() {
        let breadcrumbSections = self.ancestors.map((ancestor) => {
          return {
            text: ancestor.name,
            url: ancestor.path,
          };
        });

        breadcrumbSections.push({
          text: self.name,
          url: self.path,
        });

        return breadcrumbSections;
      },
      get hierarchy() {
        return [...self.ancestors, self];
      },
      get isIndexableByRobots() {
        // By default we want pages to be indexable.
        // Applicable for ACTIVATE_SECTIONS = false stores.
        if (!self.section) {
          return true;
        }

        if (self.section.status !== SectionStatusType.TEST_MODE) {
          return true;
        }

        return (
          self.section.status === SectionStatusType.TEST_MODE &&
          self.section.visible_to_crawlers
        );
      },
      get mainCategory() {
        return self.hierarchy[0];
      },
      get navigationName() {
        return self.name_in_navigation && self.name_in_navigation.length > 0
          ? self.name_in_navigation
          : self.name;
      },
      get parentCategory() {
        if (self.parent_id !== null && self.parent_id !== undefined) {
          return resolveIdentifier(Category, getRoot(self), self.parent_id);
        }
        return null;
      },
      get path() {
        const categories = self.hierarchy;

        if (
          self.link_type === LinkType.EXTERNAL_LINK ||
          self.link_type === LinkType.INTERNAL_LINK
        ) {
          return self.slug;
        }

        return generatePath(Paths.Category, {
          id: self.id,
          rootProductGroup: categories[0].slug,
          productGroup: categories[1] && categories[1].slug,
          subProductGroup: categories[2] && categories[2].slug,
        });
      },
    };
  });

export default Category;
